import React from 'react';
import styled from 'styled-components';

import { ICard } from '../../../types/shared';

import PrimaryButton from '../../atoms/primary-button/PrimaryButton';
import SecondaryButton from '../../atoms/secondary-button/SecondaryButton';

import { DEQ_TYPES } from '../../../utils/deq';
import { getInternalLink, ReactMarkdownExtended } from '../../../utils/generic';

import mq from '../../../styles/breakpoints';
import Colors from '../../../styles/colors';
import { Body, H1, H2 } from '../../../styles/generic';
import Spacings from '../../../styles/Spacings';

interface Props {
  title?: ICard['title'];
  subtitle?: ICard['subtitle'];
  body?: ICard['body'];
  primaryLink?: ICard['primaryLink'];
  secondaryLink?: ICard['primaryLink'];
}

const GenericCard = ({
  title,
  subtitle,
  body,
  primaryLink,
  secondaryLink,
}: Props) => {
  return (
    <Container>
      {title && <StyledH1>{title}</StyledH1>}
      {subtitle && <H2>{subtitle}</H2>}
      {body && (
        <Body>
          <ReactMarkdownExtended>
            {body.childMarkdownRemark.rawMarkdownBody}
          </ReactMarkdownExtended>
        </Body>
      )}
      {(primaryLink || secondaryLink) && (
        <ButtonWrapper>
          {primaryLink && (
            <PrimaryButton
              type={primaryLink.type}
              label={primaryLink.label}
              to={
                primaryLink.linkedPage
                  ? `/${getInternalLink(primaryLink.linkedPage)}`
                  : primaryLink.externalUrl || primaryLink.asset?.file.url || ''
              }
              icon={primaryLink.icon}
              buttonTheme={primaryLink.theme}
              deqComponentTitle={title}
              deqCustomAttributes={{
                componentType: DEQ_TYPES.primaryAnkeiler,
                elementTarget:
                  primaryLink.linkedPage?.slug ||
                  primaryLink.externalUrl ||
                  primaryLink.asset?.file ||
                  '',
              }}
              data-testid="primary-button"
            />
          )}
          {secondaryLink && (
            <SecondaryButton
              type={secondaryLink.type}
              label={secondaryLink.label}
              to={
                secondaryLink.linkedPage
                  ? `/${getInternalLink(secondaryLink.linkedPage)}`
                  : secondaryLink.externalUrl ||
                    secondaryLink.asset?.file.url ||
                    ''
              }
              icon={secondaryLink.icon}
              buttonTheme={secondaryLink.theme}
              deqComponentTitle={title}
              deqCustomAttributes={{
                componentType: DEQ_TYPES.secondaryAnkeiler,
                elementType: DEQ_TYPES.textLink,
                elementTarget:
                  secondaryLink.linkedPage?.slug ||
                  secondaryLink.externalUrl ||
                  secondaryLink.asset?.file ||
                  '',
              }}
              data-testid="secondary-button"
            />
          )}
        </ButtonWrapper>
      )}
    </Container>
  );
};

const Container = styled.section`
  background-color: ${Colors.white};
  box-shadow: ${({ theme }) => theme.elevations.abnElevationTile};
  padding: ${Spacings.S};

  ${mq.from.breakpoint.M`
    padding: ${Spacings.L};
  `}

  ${mq.from.breakpoint.L`
    padding: ${Spacings.M};
  `}

  ${mq.from.breakpoint.XL`
    padding: ${Spacings.XL};
  `}
`;

const StyledH1 = styled(H1)`
  word-break: break-word;
  word-wrap: break-word;
`;

const ButtonWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${Spacings.M};
  margin-top: ${Spacings.M};
`;

export default GenericCard;
